<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { getImageClassRounded } from '@/happyTheme/utils/catalogCategory/getImageClassRounded'
import { checkTruncationHappy } from '@/happyTheme/utils/product/checkTrunctationHappy'
import type { Product } from '@/storage/products'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { formatPrice } from '@/event/locale'
import { getProductName } from '@/utils/product/getProductName'
import { nextTick, onMounted, ref } from 'vue'

const props = defineProps<{
    product: Product
    description: string
}>()

const imageSizes = ref<Record<number, { width: number; height: number }>>({})

const maxLines = ref<number>(1)
const descriptionElement = ref<HTMLElement>()
const divDescriptionElement = ref<HTMLElement>()
const buttonElementSelect = ref<HTMLElement>()

function onImageLoad(categoryId: number, event: Event) {
    const img = event.target as HTMLImageElement
    imageSizes.value[categoryId] = { width: img.naturalWidth, height: img.naturalHeight }
}

onMounted(() => {
    nextTick(() => {
        checkTruncationHappy(
            descriptionElement,
            divDescriptionElement,
            buttonElementSelect,
            maxLines
        )
        window.addEventListener('resize', () =>
            checkTruncationHappy(
                descriptionElement,
                divDescriptionElement,
                buttonElementSelect,
                maxLines
            )
        )
    })
})
</script>

<template>
    <section class="section-parent-card-product-happy">
        <section class="section-card-product-happy">
            <div class="div-card-product-happy">
                <img
                    class="img-card-product-happy"
                    :class="{
                        'object-contain': props.product.hasCleanImg,
                        'object-cover object-center': !props.product.hasCleanImg
                    }"
                    :src="`${URL_IMAGE}${props.product.image}`"
                    :alt="getProductName(props.product)"
                    @load="onImageLoad(props.product.product_id, $event)"
                />
            </div>
            <div class="div-description-card-product-happy" ref="divDescriptionElement">
                <section class="flex flex-col h-full w-full justify-between">
                    <div
                        class="div-description-child-card-product-happy"
                        ref="descriptionElement"
                    >
                        <h3 class="text-product-name-happy">
                            {{ getProductName(props.product) }}
                        </h3>
                        <p class="text-price-product-happy">
                            {{ formatPrice(props.product.price) }}
                        </p>
                    </div>
                    <div class="text-container">
                        <p
                            v-if="props.description !== 'Aucune description'"
                            class="text-description-product-happy"
                            :class="`line-clamp-${maxLines}`"
                            :style="{ '-webkit-line-clamp': maxLines }"
                        >
                            {{ props.description }}
                        </p>
                    </div>
                    <div
                        class="div-button-select-product-full-happy"
                        ref="buttonElementSelect"
                    >
                        {{ $t('select') }}
                    </div>
                </section>
            </div>
        </section>
    </section>
</template>

<style scoped>
.section-parent-card-product-happy {
    @apply w-full h-full flex justify-center items-center p-0.5;
}

.section-card-product-happy {
    @apply flex flex-col justify-end items-center min-h-52 w-full rounded-lg relative overflow-hidden border-[1.5px];
    /* refactored */
    @apply border-t-accent;
    @apply happy-ifo-dark:border-t-border;
}

.div-card-product-happy {
    @apply absolute inset-0 min-w-full h-full z-0 flex pb-16;
}
.img-card-product-happy {
    @apply w-full;
}

.div-description-card-product-happy {
    @apply absolute w-full h-24 p-2 flex flex-col;
    @apply justify-start bottom-0 left-0 overflow-hidden z-10 gap-1.5;
    background-color: hsla(0, 0%, 20%, 0.7);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
}
/* .div-description-card-product-happy::before {
    content: '';
    @apply inset-0 z-0;
} */
.div-description-card-product-happy > * {
    @apply relative z-20;
}
.div-description-child-card-product-happy {
    @apply w-full flex justify-between items-start;
    @apply flex-shrink-0;
}

.text-container {
    @apply overflow-hidden;
}

.text-product-name-happy {
    @apply text-sm font-semibold text-wrap line-clamp-2 text-bg-happy-light;
}
.text-price-product-happy {
    /* refactored */
    @apply text-sm font-semibold text-nowrap text-t-accent;
    @apply happy-ifo-dark:text-t-accent;
}
.text-description-product-happy {
    @apply text-xs text-ellipsis text-bg-happy-light;
    @apply flex-1 min-h-0 overflow-hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.div-button-select-product-full-happy {
    @apply w-full h-8 text-center text-sm rounded-md mt-2 cursor-pointer shadow-sm;
    @apply font-bold flex justify-center items-center;
    /* refactored */
    @apply bg-t-accent text-t-on-accent shadow-t-border;
    /* refactored: ifo-dark/light pill style — dark-transparan bg + amber
       border + amber text. Ekle butonunun (button-add-to-cart-happy)
       dark-pill tarzına uyumlu. */
    @apply happy-ifo-dark:text-t-accent
           happy-ifo-dark:bg-t-accent/10
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:shadow-none
           happy-ifo-dark:rounded-full
           happy-ifo-dark:tracking-wide;
}

/* Active */

.section-card-product-happy:active,
.section-card-product-happy:focus {
    /* refactored */
    @apply bg-t-accent-hover border-t-accent-hover;
}

/* Hover */

@media (hover: hover) {
    .section-card-product-happy:hover {
        /* refactored */
        @apply bg-t-accent-hover border-t-accent-hover;
    }
}
</style>
